// Pure recency-ordered byte budget for GPU textures.
//
// Tracks a set of keys, each with a byte cost, ordered least-recently-used →
// most-recently-used. When admitting a new entry would exceed the cap, it plans
// which existing entries to evict (LRU first), skipping any that are *pinned*.
//
// This is deliberately Phaser-free so it can be unit-tested in isolation; the
// caller (`ScopedLoader`) owns the actual `scene.textures.remove()` side-effects
// and drives this class in lock-step. Recency is encoded by Map insertion order:
// `add` and `touch` (re)insert at the tail, so `keys()` yields LRU → MRU.

export interface EvictionPlan {
  /** Whether the incoming bytes fit after applying `evict`. */
  fits: boolean;
  /** Keys to evict (LRU-first). Empty when it already fits or cannot fit. */
  evict: string[];
}

export class LruBudget {
  private readonly _max: number;
  private readonly _bytes = new Map<string, number>(); // insertion order = LRU → MRU
  private _pinned: Set<string> = new Set();
  private _total = 0;

  constructor(maxBytes: number) {
    this._max = maxBytes;
  }

  get totalBytes(): number {
    return this._total;
  }

  get maxBytes(): number {
    return this._max;
  }

  has(key: string): boolean {
    return this._bytes.has(key);
  }

  /** All tracked keys, LRU → MRU order. */
  keys(): string[] {
    return [...this._bytes.keys()];
  }

  /** Record a new entry at the MRU end. Assumes room has already been made. */
  add(key: string, bytes: number): void {
    if (this._bytes.has(key)) this._total -= this._bytes.get(key)!;
    this._bytes.set(key, bytes);
    this._total += bytes;
  }

  /** Mark `key` most-recently-used (no-op if absent). */
  touch(key: string): void {
    const bytes = this._bytes.get(key);
    if (bytes === undefined) return;
    this._bytes.delete(key);
    this._bytes.set(key, bytes); // re-insert at tail
  }

  /** Remove `key`, returning the bytes it freed (0 if absent). */
  remove(key: string): number {
    const bytes = this._bytes.get(key);
    if (bytes === undefined) return 0;
    this._bytes.delete(key);
    this._total -= bytes;
    return bytes;
  }

  /** Replace the protected set — pinned keys are never chosen for eviction. */
  setPinned(keys: Iterable<string>): void {
    this._pinned = new Set(keys);
  }

  isPinned(key: string): boolean {
    return this._pinned.has(key);
  }

  /**
   * Plan (without mutating) how to admit `incomingBytes` more. Walks LRU → MRU,
   * skipping pinned keys, until the incoming bytes would fit. If even evicting
   * every unpinned entry can't make room, returns `{fits:false, evict:[]}` — the
   * caller should then refuse the new load rather than evict for nothing.
   */
  planFor(incomingBytes: number): EvictionPlan {
    if (this._total + incomingBytes <= this._max) return { fits: true, evict: [] };
    const evict: string[] = [];
    let freed = 0;
    for (const [key, bytes] of this._bytes) {
      if (this._pinned.has(key)) continue;
      evict.push(key);
      freed += bytes;
      if (this._total - freed + incomingBytes <= this._max) return { fits: true, evict };
    }
    return { fits: false, evict: [] };
  }

  clear(): void {
    this._bytes.clear();
    this._pinned.clear();
    this._total = 0;
  }
}
